/**
 * Interactive classification controls for track popups: affiliation + category
 * dropdowns that override a track's auto-assigned classification. The override
 * recolors the marker (via the track-classification store) and persists.
 */

import type { CotAffiliation } from '@vtak/shared';
import type { MarkerData } from './marker-state';
import { useTrackClassificationStore } from '@/stores/track-classification-store';

/** Affiliation options shown in the dropdown. */
const AFFILIATIONS: ReadonlyArray<{ value: CotAffiliation; label: string }> = [
  { value: 'friendly', label: 'Friendly' },
  { value: 'hostile', label: 'Hostile' },
  { value: 'neutral', label: 'Neutral' },
  { value: 'unknown', label: 'Unknown' },
];

/** Category options per domain (derived from the layer prefix). */
const CATEGORIES_BY_DOMAIN: ReadonlyMap<string, readonly string[]> = new Map([
  ['air', ['unknown', 'military', 'civilian', 'commercial']],
  ['maritime', ['unknown', 'military', 'commercial', 'fishing', 'civilian']],
  ['space', ['unknown', 'military', 'commercial', 'scientific']],
  ['incident', ['unknown', 'wildfire', 'hazard']],
  ['osint', ['unknown', 'news', 'conflict']],
]);

/** Build the classification dropdown block appended inside a track popup. */
export function buildClassificationControls(data: MarkerData): string {
  const current = useTrackClassificationStore.getState().get(data.uid);
  const affiliation = current?.affiliation ?? affiliationOf(data.cotType);
  const categories = categoriesForLayer(data.layer);
  const category = current?.category ?? categories[0];

  return [
    '<div style="margin-top:8px;padding-top:8px;border-top:1px solid rgba(255,255,255,0.12)">',
    '<div style="font-size:10px;text-transform:uppercase;letter-spacing:0.08em;color:#9aa;margin-bottom:4px">Classify</div>',
    '<div style="display:flex;gap:6px">',
    selectHtml(data.uid, 'affiliation', AFFILIATIONS, affiliation),
    selectHtml(data.uid, 'category', categories.map((c) => ({ value: c, label: titleCase(c) })), category),
    '</div>',
    '</div>',
  ].join('');
}

/** Wire change listeners on the classification dropdowns for a uid (document-scoped). */
export function attachClassificationListeners(uid: string): void {
  for (const select of selectsFor(uid)) {
    select.addEventListener('change', () => applyFromSelects(uid));
  }
}

/** Read both dropdowns and store the override. */
function applyFromSelects(uid: string): void {
  const selects = selectsFor(uid);
  const affiliation = valueOf(selects, 'affiliation') as CotAffiliation | undefined;
  const category = valueOf(selects, 'category') ?? 'unknown';
  if (affiliation) {
    useTrackClassificationStore.getState().set(uid, { affiliation, category });
  }
}

/** The classification select elements for a uid currently in the document. */
function selectsFor(uid: string): HTMLSelectElement[] {
  return Array.from(document.querySelectorAll('select[data-uid]')).filter(
    (el): el is HTMLSelectElement => el instanceof HTMLSelectElement && el.dataset.uid === uid,
  );
}

/** Current value of the dropdown for a given field. */
function valueOf(selects: HTMLSelectElement[], field: string): string | undefined {
  return selects.find((s) => s.dataset.field === field)?.value;
}

/** Build a styled select element for the popup. */
function selectHtml(
  uid: string,
  field: string,
  options: ReadonlyArray<{ value: string; label: string }>,
  selected: string,
): string {
  const opts = options
    .map((o) => `<option value="${o.value}"${o.value === selected ? ' selected' : ''}>${o.label}</option>`)
    .join('');
  return `<select data-uid="${uid}" data-field="${field}" style="flex:1;min-width:0;background:#1b1b1b;color:#eee;border:1px solid #444;border-radius:4px;padding:3px;font-size:11px">${opts}</select>`;
}

/** Category list for a layer (prefix before ':' is the domain). */
function categoriesForLayer(layer: string): readonly string[] {
  const domain = layer.split(':')[0];
  return CATEGORIES_BY_DOMAIN.get(domain) ?? ['unknown'];
}

/** Affiliation implied by a CoT type's 2nd segment. */
function affiliationOf(cotType: string): CotAffiliation {
  const char = cotType.split('-')[1];
  if (char === 'f') return 'friendly';
  if (char === 'h') return 'hostile';
  if (char === 'n') return 'neutral';
  return 'unknown';
}

/** Capitalize the first letter. */
function titleCase(value: string): string {
  return value.length === 0 ? value : value[0].toUpperCase() + value.slice(1);
}
